/** Unauthenticated fetch helper for the public quote share view.
 *
 * Distinct from ``apiClient`` because:
 *  - No ``credentials: include`` (we don't want the contractor's
 *    session cookie tagging along when a client opens the link)
 *  - Errors don't reach the auth-redirect logic
 *
 * Same ``ApiError`` shape so ``instanceof ApiError`` works in the
 * public components.
 */

import { ApiError, apiBase } from "./client";

async function publicRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    method,
    credentials: "omit",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = (await res.json()) as { detail?: unknown };
      detail = formatPublicErrorDetail(data?.detail) || res.statusText;
    } catch {
      /* keep statusText */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Multipart POST. Used by the public comment form when the homeowner
 *  attaches files; we don't set ``Content-Type`` ourselves so the
 *  browser writes the correct multipart boundary. */
async function publicMultipartPost<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    method: "POST",
    credentials: "omit",
    body: form,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = (await res.json()) as { detail?: unknown };
      detail = formatPublicErrorDetail(data?.detail) || res.statusText;
    } catch {
      /* keep statusText */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const publicApiClient = {
  get: <T>(path: string) => publicRequest<T>("GET", path),
  post: <T>(path: string, body?: unknown) => publicRequest<T>("POST", path, body),
  postMultipart: <T>(path: string, form: FormData) => publicMultipartPost<T>(path, form),
};

function formatPublicErrorDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (!detail) return "";
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e === "object") {
          const obj = e as Record<string, unknown>;
          const msg = typeof obj.msg === "string" ? obj.msg : JSON.stringify(obj);
          return msg;
        }
        return JSON.stringify(e);
      })
      .join("; ");
  }
  return JSON.stringify(detail);
}
